import { ContactDraft, QuickButton, SessionContext } from '../conversation/types';
import { caseChatLink, caseQuickButtons, formatCaseDetail, formatCaseList, formatDelayExplanation, MAX_CASE_PICKS } from '../caseDetails/formatCase';
import { CaseSummary } from '../caseDetails/caseSummary';
import { buildTriagePayload } from '../triage/triageService';
import { TriageContact, TriagePayload, TriageReason } from '../triage/types';
import * as R from './responses';
import { Personality } from './personalities';
import { BotTurn, CASE_BUTTON_PREFIX, CASE_LOOKUP_PRESET_ID, CASE_QUESTION_PRESET_ID, Preset, PresetContext, PresetInput, PRESET_BUTTON_PREFIX, selectedCaseFrom } from './types';

export const CONTACT_SUPPORT_PRESET_ID = 'contact_support';
export const WHO_TO_CONTACT_PRESET_ID = 'who_to_contact';
export const CASE_STATUS_PRESET_ID = 'case_status';
export const CASE_DELAY_PRESET_ID = 'case_delay';
export const END_CHAT_PRESET_ID = 'end_chat';

export const presetButton = (preset: Pick<Preset, 'id' | 'label'>): QuickButton => ({
  label: preset.label,
  value: `${PRESET_BUTTON_PREFIX}${preset.id}`,
});


export const buttonFor = (presets: Preset[], id: string, label?: string): QuickButton[] => {
  const preset = presets.find((p) => p.id === id);
  return preset ? [label ? { ...presetButton(preset), label } : presetButton(preset)] : [];
};

/** The in-conversation way to finish: offered with the menu, so ending the chat is always one tap away. */
export const END_CHAT_BUTTON_LABEL = "That's all, thanks";

export const menuButtons = (presets: Preset[]): QuickButton[] => [
  ...presets.filter((p) => p.showInMenu).map(presetButton),
  ...buttonFor(presets, END_CHAT_PRESET_ID, END_CHAT_BUTTON_LABEL),
];

/** Context fields that belong to the "Have support contact me" flow; cleared when it ends. */
export const CLEAR_CONTACT_FLOW: Partial<SessionContext> = {
  pendingIntentId: undefined,
  contactStep: undefined,
  contactDraft: undefined,
  lastUnansweredText: undefined,
  caseQuestionDraft: undefined,
};

/** Buttons offered after Evie has answered what it can about a case. */
const escalationButtons = (ctx: PresetContext, withDesignTeam: boolean): QuickButton[] => [
  ...(withDesignTeam ? buttonFor(ctx.presets, WHO_TO_CONTACT_PRESET_ID) : []),
  ...buttonFor(ctx.presets, CONTACT_SUPPORT_PRESET_ID),
  ...buttonFor(ctx.presets, 'case_status', 'Check another case'),
];

const caseDetailTurn = (ctx: PresetContext, c: CaseSummary, intro?: string): BotTurn => ({
  messages: [intro ? `${intro}\n\n${formatCaseDetail(c)}` : formatCaseDetail(c)],
  quickButtons: [
    ...buttonFor(ctx.presets, 'case_status', 'Check another case'),
    ...buttonFor(ctx.presets, CASE_DELAY_PRESET_ID),
    ...buttonFor(ctx.presets, WHO_TO_CONTACT_PRESET_ID),
    ...buttonFor(ctx.presets, CONTACT_SUPPORT_PRESET_ID),
  ],
  context: { selectedCaseRefNo: c.caseRefNo, selectedCaseId: c.id, pendingIntentId: undefined, helpAttempted: true },
  caseRefNo: c.caseRefNo,
});

async function submitTriage(
  ctx: PresetContext,
  reason: TriageReason,
  userMessage?: string,
  contact?: TriageContact,
  priority?: TriagePayload['priority'],
  caseOverride?: CaseSummary
) {
  const context = ctx.session.context;
  const selectedCase = caseOverride ? { id: caseOverride.id, caseRefNo: caseOverride.caseRefNo } : selectedCaseFrom(context);
  const effectivePriority = priority ?? context.escalationPriority;
  const payload = buildTriagePayload({
    session: ctx.session,
    user: ctx.user,
    reason,
    selectedCase,
    userMessage,
    contact,
    priority: effectivePriority,
    sentiment: context.escalationPriority === 'high' ? 'frustrated' : undefined,
  });
  return ctx.triage.submit(payload);
}

/**
 * The first time someone asks for support, Evie tries to help herself: recent cases, the delay
 * explanation, a problem report, and only then a support call-back. Asking again goes straight through.
 */
export async function deflectToSelfService(ctx: PresetContext, message?: string): Promise<BotTurn> {
  const recent = await ctx.caseDetails.listRecent(MAX_CASE_PICKS).catch(() => [] as CaseSummary[]);
  return {
    messages: [message ?? R.beforeSupportPrompt()],
    quickButtons: [
      ...caseQuickButtons(recent),
      ...(recent.length > 0 ? buttonFor(ctx.presets, CASE_DELAY_PRESET_ID) : []),
      ...buttonFor(ctx.presets, 'report_problem'),
      ...buttonFor(ctx.presets, CONTACT_SUPPORT_PRESET_ID, 'I still need support'),
    ],
    context: { supportAsks: (ctx.session.context.supportAsks ?? 0) + 1, pendingIntentId: undefined },
  };
}

/** Whether a support call-back can be offered without first trying self-service. */
export const escalationEarned = (context: SessionContext, draft: ContactDraft, summary?: string): boolean =>
  Boolean(context.escalationUnlocked)
  || Boolean(context.helpAttempted)
  || Boolean(draft.reason)
  || Boolean(summary)
  || (context.supportAsks ?? 0) >= 1;

/**
 * The case the user is asking about: the number they typed, else the one they last looked at. A typed
 * number that is not found resolves to nothing, never to a different case.
 */
async function resolveCase(ctx: PresetContext, input: PresetInput): Promise<CaseSummary | null> {
  if (input.caseRefNo) return ctx.caseDetails.findByRefNo(input.caseRefNo);
  const selected = ctx.session.context.selectedCaseRefNo;
  if (selected) return ctx.caseDetails.findByRefNo(selected);
  return null;
}

const caseStatus: Preset = {
  id: CASE_STATUS_PRESET_ID,
  label: 'How is my case?',
  showInMenu: true,
  priority: 10,
  keywords: ['case', 'cases', 'status', 'update', 'order', 'eta', 'ready', 'done', 'progress'],
  patterns: [
    /how('s| is| are) my (case|cases|order)/,
    /(case|order) (status|update|progress)/,
    /where('s| is) my (case|order)/,
    /(status|update) (of|on|for) my (case|order)/,
  ],
  async handler(ctx) {
    const cases = await ctx.caseDetails.listRecent(MAX_CASE_PICKS);
    if (cases.length === 0) {
      return { messages: [R.noCases(ctx.personality)], quickButtons: menuButtons(ctx.presets) };
    }
    if (cases.length === 1) {
      return caseDetailTurn(ctx, cases[0], R.singleCaseIntro(ctx.personality, ctx.user.firstName));
    }
    return {
      messages: [formatCaseList(cases, ctx.user.firstName, undefined, undefined, ctx.personality)],
      quickButtons: caseQuickButtons(cases, MAX_CASE_PICKS),
      context: { pendingIntentId: undefined },
    };
  },
};

const caseLookup: Preset = {
  id: CASE_LOOKUP_PRESET_ID,
  label: 'Look up a case',
  showInMenu: false,
  priority: 50,
  keywords: [],
  async handler(ctx, input: PresetInput) {
    const refNo = input.caseRefNo;
    if (!refNo) {
      return { messages: [R.whichCase(ctx.personality)], quickButtons: menuButtons(ctx.presets) };
    }
    const found = await ctx.caseDetails.findByRefNo(refNo);
    if (found) return caseDetailTurn(ctx, found);

    const recent = await ctx.caseDetails.listRecent(MAX_CASE_PICKS);
    return {
      messages: [R.caseNotFound(ctx.personality, refNo)],
      quickButtons: recent.length > 0 ? caseQuickButtons(recent) : menuButtons(ctx.presets),
    };
  },
};

const listByStatusTurn = async (
  ctx: PresetContext,
  statusFilter: 'Case For Dentist Approval' | 'Case In Progress',
  intro: string,
  empty: string
): Promise<BotTurn> => {
  const cases = await ctx.caseDetails.listByStatus(statusFilter, MAX_CASE_PICKS);
  if (cases.length === 0) return { messages: [empty], quickButtons: menuButtons(ctx.presets) };
  if (cases.length === 1) return caseDetailTurn(ctx, cases[0], intro);
  return { messages: [formatCaseList(cases, ctx.user.firstName, intro, undefined, ctx.personality)], quickButtons: caseQuickButtons(cases) };
};

const casesForApproval: Preset = {
  id: 'cases_for_approval',
  label: 'Anything waiting on me?',
  showInMenu: true,
  priority: 20,
  keywords: ['approval', 'approve', 'waiting', 'pending', 'review', 'sign off'],
  patterns: [/waiting (on|for) me/, /need(s)? my approval/],
  handler: (ctx) => listByStatusTurn(ctx, 'Case For Dentist Approval', R.forApprovalIntro(ctx.personality), R.nothingForApproval(ctx.personality)),
};

const casesInProgress: Preset = {
  id: 'cases_in_progress',
  label: "What's in progress?",
  showInMenu: true,
  priority: 15,
  keywords: ['in progress', 'working', 'lab', 'design', 'designing', 'ongoing', 'active'],
  patterns: [/what('s| is) in progress/, /being (worked|designed)/],
  handler: (ctx) => listByStatusTurn(ctx, 'Case In Progress', R.inProgressIntro(ctx.personality), R.nothingInProgress(ctx.personality)),
};

const howToTrack: Preset = {
  id: 'how_to_track',
  label: 'How do I track a case?',
  showInMenu: true,
  priority: 5,
  keywords: ['track', 'tracking', 'find', 'follow', 'locate', 'where'],
  patterns: [/how (do|can) i (track|find|follow)/],
  async handler(ctx) {
    return { messages: [R.HOW_TO_TRACK], quickButtons: buttonFor(ctx.presets, 'case_status') };
  },
};

/**
 * "Why is it taking so long?" Explains what the Hub knows about the case (who it is waiting on,
 * expected return date, last update) and then points at the design team and support.
 * Patterns only: bare words like "long", "late" or "waiting" belong to other intents or to noise.
 */
const caseDelay: Preset = {
  id: CASE_DELAY_PRESET_ID,
  label: 'Why is it taking so long?',
  showInMenu: false,
  priority: 35,
  keywords: [],
  patterns: [
    /taking (so|too|this) long/,
    /\b(delay|delayed|overdue|behind schedule|running late)\b/,
    /still waiting/,
    /how (much )?longer/,
    /when (will|is) (it|my case|this|the case) (be )?(back|ready|done|finished)/,
    /why (is|has|does) (it|this|my case|the case)/,
  ],
  async handler(ctx, input) {
    let found = await resolveCase(ctx, input);
    if (!found) {
      const recent = await ctx.caseDetails.listRecent(MAX_CASE_PICKS);
      if (recent.length === 0) return { messages: [R.noCases(ctx.personality)], quickButtons: menuButtons(ctx.presets) };
      if (recent.length === 1) found = recent[0];
      else {
        return {
          messages: [R.whichCaseIsSlow(ctx.personality)],
          quickButtons: caseQuickButtons(recent),
          context: { pendingIntentId: undefined },
        };
      }
    }
    return {
      messages: [formatDelayExplanation(found)],
      quickButtons: escalationButtons(ctx, true),
      context: { selectedCaseRefNo: found.caseRefNo, selectedCaseId: found.id, pendingIntentId: undefined, helpAttempted: true },
      caseRefNo: found.caseRefNo,
    };
  },
};

/**
 * "Who should I reach out to?" With a case in context, sends the dentist to that case's Messages tab,
 * which the design team reads. Without one, asks which case, or offers a support call-back.
 */
const whoToContact: Preset = {
  id: WHO_TO_CONTACT_PRESET_ID,
  label: 'Message the design team',
  showInMenu: false,
  priority: 36,
  keywords: ['human', 'person', 'agent', 'someone', 'support', 'representative', 'team', 'designer', 'contact'],
  patterns: [
    /who (should|do|can|could) i (reach|contact|talk|speak|message|email|call|ask)/,
    /who('s| is) (handling|working on|looking after)/,
    /reach out/,
    /(talk|speak|chat) (to|with) (a |an |someone|somebody)/,
    /real (person|human)/,
    /customer (service|support)/,
    /(message|contact|ask) the (design )?team/,
  ],
  async handler(ctx, input) {
    const found = await resolveCase(ctx, input);
    if (found) {
      return {
        messages: [R.DESIGN_TEAM_HANDOFF(found.caseRefNo, caseChatLink(found))],
        quickButtons: [
          ...buttonFor(ctx.presets, CONTACT_SUPPORT_PRESET_ID),
          ...buttonFor(ctx.presets, 'case_status', 'Check another case'),
        ],
        context: { selectedCaseRefNo: found.caseRefNo, selectedCaseId: found.id, pendingIntentId: undefined, helpAttempted: true },
        caseRefNo: found.caseRefNo,
      };
    }
    const recent = await ctx.caseDetails.listRecent(MAX_CASE_PICKS);
    // Asking who to contact is itself a support ask: tapping the support button below must not deflect again.
    return {
      messages: [R.whichCaseForContact()],
      quickButtons: [
        ...caseQuickButtons(recent),
        ...buttonFor(ctx.presets, CONTACT_SUPPORT_PRESET_ID),
        ...buttonFor(ctx.presets, 'help', 'Back to menu'),
      ],
      context: { pendingIntentId: undefined, supportAsks: (ctx.session.context.supportAsks ?? 0) + 1 },
    };
  },
};

const SEND_WORDS = /^(send( it| this| that)?( to (the )?(designer|design team|team))?( please)?|yes( please)?|yep|yeah|sure|ok(ay)?|go ahead|do it)[.!]?$/i;
const SKIP_WORDS = /^(no( thanks?| thank you)?|nope|don't send( it)?|do not send( it)?|cancel|never ?mind|leave it|forget it)[.!]?$/i;

const caseQuestionButtons = (): QuickButton[] => [
  { label: 'Send to designer', value: 'Send to designer' },
  { label: 'No thanks', value: 'No thanks' },
];

/**
 * A case number with an ask that is not about status ("change the shade to A2 on 721323918"). Evie
 * cannot act on the case, so she offers to post the dentist's words to the case's Messages tab, which
 * the design team reads, and escalates the conversation ticket so support knows. Nothing is posted
 * until the dentist confirms; anything else they type replaces the draft.
 */
const caseQuestion: Preset = {
  id: CASE_QUESTION_PRESET_ID,
  label: 'Ask the design team about a case',
  showInMenu: false,
  priority: 34,
  keywords: [],
  async handler(ctx, input) {
    const context = ctx.session.context;
    const draft = context.pendingIntentId === CASE_QUESTION_PRESET_ID ? context.caseQuestionDraft : undefined;
    const text = String(input.text ?? '').trim();

    if (draft) {
      const link = caseChatLink({ id: draft.caseId });
      const done = (message: string): BotTurn => ({
        messages: [message],
        quickButtons: [...buttonFor(ctx.presets, 'case_status', 'Check another case'), ...buttonFor(ctx.presets, END_CHAT_PRESET_ID, END_CHAT_BUTTON_LABEL)],
        context: { pendingIntentId: undefined, caseQuestionDraft: undefined },
        caseRefNo: draft.caseRefNo,
      });
      if (SKIP_WORDS.test(text)) return done(R.CASE_MESSAGE_SKIPPED(draft.caseRefNo, link));
      if (SEND_WORDS.test(text)) {
        const result = draft.caseId
          ? await ctx.caseMessages.post(draft.caseId, `${R.CASE_MESSAGE_PREFIX} ${draft.text}`)
          : { accepted: false };
        return done(result.accepted ? R.CASE_MESSAGE_SENT(draft.caseRefNo, link) : R.CASE_MESSAGE_FAILED(draft.caseRefNo, link));
      }
      return {
        messages: [R.CASE_QUESTION_REWORDED(draft.caseRefNo, text)],
        quickButtons: caseQuestionButtons(),
        context: { pendingIntentId: CASE_QUESTION_PRESET_ID, caseQuestionDraft: { ...draft, text } },
        caseRefNo: draft.caseRefNo,
      };
    }

    const found = await resolveCase(ctx, input);
    if (!found) {
      const recent = await ctx.caseDetails.listRecent(MAX_CASE_PICKS);
      return {
        messages: [input.caseRefNo ? R.caseNotFound(ctx.personality, input.caseRefNo) : R.whichCase(ctx.personality)],
        quickButtons: recent.length > 0 ? caseQuickButtons(recent) : menuButtons(ctx.presets),
        context: { pendingIntentId: undefined },
      };
    }

    // Support hears about every change request (ticket → Open); a failed escalation never blocks the reply.
    if (ctx.user.email) {
      await submitTriage(ctx, 'case_question', text, { email: ctx.user.email }, undefined, found).catch(() => undefined);
    }

    return {
      messages: [R.CASE_QUESTION_CONFIRM(found.caseRefNo, text)],
      quickButtons: caseQuestionButtons(),
      context: {
        selectedCaseRefNo: found.caseRefNo,
        selectedCaseId: found.id,
        pendingIntentId: CASE_QUESTION_PRESET_ID,
        caseQuestionDraft: { caseRefNo: found.caseRefNo, caseId: found.id, text },
        helpAttempted: true,
      },
      caseRefNo: found.caseRefNo,
    };
  },
};

const CANCEL_WORDS = /^(cancel|never ?mind|menu|stop|forget it|no thanks?|leave it)[.!]?$/i;
const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const MIN_PHONE_DIGITS = 7;
const MAX_CONTACT_ATTEMPTS = 2;

export function parseContact(raw: string): TriageContact | null {
  const text = String(raw ?? '').trim();
  const email = EMAIL_RE.exec(text)?.[0];
  if (email) return { email };
  const digits = (text.match(/\d/g) || []).length;
  if (digits >= MIN_PHONE_DIGITS && digits <= 20 && /^[\d\s()+.-]+$/.test(text)) return { phone: text };
  return null;
}

/** Buttons offered after a hand-off: change where support replies, or go back. */
const handoffButtons = (ctx: PresetContext): QuickButton[] => [
  { label: R.USE_DIFFERENT_EMAIL_LABEL, value: R.USE_DIFFERENT_EMAIL_LABEL },
  { label: R.CALL_ME_LABEL, value: R.CALL_ME_LABEL },
  ...buttonFor(ctx.presets, 'help', 'Back to menu'),
];

const DIFFERENT_EMAIL_WORDS = /\b(different|another|other|change)\b.*\bemail\b|\bemail\b.*\b(different|another|other|instead)\b/i;
const CALL_ME_WORDS = /\b(call|phone|ring)\b/i;

/**
 * After a hand-off, only these keep the contact flow alive: a tap on one of its buttons, words to the
 * same effect, a typed email or number, or a cancel. Anything else is a new question for the engine.
 */
export const isHandoffFollowUp = (raw: string): boolean => {
  const text = String(raw ?? '').trim();
  return text === R.USE_DIFFERENT_EMAIL_LABEL
    || text === R.CALL_ME_LABEL
    || DIFFERENT_EMAIL_WORDS.test(text)
    || CALL_ME_WORDS.test(text)
    || CANCEL_WORDS.test(text)
    || parseContact(text) !== null;
};

/** The confirmation for a filed ticket: by email or by phone, with the reply window. */
const filedMessage = (contact: TriageContact, ticketRef?: string): string =>
  contact.phone ? R.HANDOFF_PHONE(contact.phone, ticketRef) : R.HANDOFF_EMAIL(String(contact.email), ticketRef);

/**
 * Files the ticket with the account email straight away and tells the dentist, in one line, where and
 * when support will reply. Only a session with no account email is asked for a contact first. Shared by
 * "Have support contact me", "Report a problem" and the engine's LLM escalation.
 */
export async function handOffToSupport(ctx: PresetContext, draft: ContactDraft): Promise<BotTurn> {
  const email = (ctx.user.email || '').trim();
  if (!email) {
    return {
      messages: [R.askContactPrompt()],
      quickButtons: buttonFor(ctx.presets, 'help', 'Back to menu'),
      context: { contactStep: 'contact', contactDraft: draft, pendingIntentId: CONTACT_SUPPORT_PRESET_ID },
    };
  }
  const contact: TriageContact = { email };
  const result = await submitTriage(ctx, draft.reason ?? 'talk_to_human', draft.summary, contact, draft.priority);
  if (!result.accepted) {
    return { messages: [R.TRIAGE_UNAVAILABLE], quickButtons: menuButtons(ctx.presets), context: CLEAR_CONTACT_FLOW };
  }
  if (result.mode === 'noop') {
    return { messages: [R.TRIAGE_NOTED], quickButtons: menuButtons(ctx.presets), context: CLEAR_CONTACT_FLOW };
  }
  return {
    messages: [R.HANDOFF_EMAIL(email, result.ticketRef)],
    quickButtons: handoffButtons(ctx),
    context: {
      contactStep: 'handed_off',
      contactDraft: { ...draft, attempts: 0, handoffContact: contact },
      pendingIntentId: CONTACT_SUPPORT_PRESET_ID,
    },
  };
}

/**
 * "Have support contact me": collects what they need (unless Evie already has the unanswered
 * question or a problem report), files the ticket with the account email, and then lets the dentist
 * swap in a different email or ask for a call instead. Both re-escalate the same ticket.
 */
const contactSupport: Preset = {
  id: CONTACT_SUPPORT_PRESET_ID,
  label: 'Have support contact me',
  showInMenu: false,
  priority: 40,
  keywords: ['call', 'phone', 'ticket', 'callback'],
  patterns: [/(contact|call|email|phone|ring) me/, /(get|be) in touch/, /raise a ticket/],
  async handler(ctx, input) {
    const context = ctx.session.context;
    const draft: ContactDraft = context.contactDraft || {};
    const step = context.contactStep;
    const text = String(input.text ?? '').trim();
    const isLabel = text.toLowerCase() === this.label.toLowerCase();

    if (!step || isLabel) {
      const summary = draft.summary ?? context.lastUnansweredText;
      if (!escalationEarned(context, draft, summary)) return deflectToSelfService(ctx);
      if (!summary) {
        return {
          messages: [R.askSummaryPrompt(ctx.personality)],
          quickButtons: buttonFor(ctx.presets, 'help', 'Back to menu'),
          context: { contactStep: 'summary', contactDraft: {}, pendingIntentId: CONTACT_SUPPORT_PRESET_ID },
        };
      }
      const reason: ContactDraft['reason'] = draft.reason ?? (draft.summary ? 'talk_to_human' : 'unresolved');
      return handOffToSupport(ctx, { ...draft, summary, reason });
    }

    if (CANCEL_WORDS.test(text)) {
      return { messages: [R.contactCancelled(ctx.personality)], quickButtons: menuButtons(ctx.presets), context: CLEAR_CONTACT_FLOW };
    }

    if (step === 'summary') {
      return handOffToSupport(ctx, { ...draft, summary: text, reason: draft.reason ?? 'talk_to_human' });
    }

    const askFor = (next: 'email' | 'phone'): BotTurn => ({
      messages: [next === 'email' ? R.ASK_DIFFERENT_EMAIL : R.ASK_PHONE],
      quickButtons: buttonFor(ctx.presets, 'help', 'Back to menu'),
      context: { contactStep: next, contactDraft: { ...draft, attempts: 0 }, pendingIntentId: CONTACT_SUPPORT_PRESET_ID },
    });

    const typed = parseContact(text);

    if (step === 'handed_off') {
      // The ticket is filed. A tap (or words to that effect) changes where support replies; a typed
      // address or number is taken as that change straight away; anything else is a new question.
      if (typed) return fileChange(ctx, draft, typed);
      if (text === R.USE_DIFFERENT_EMAIL_LABEL || DIFFERENT_EMAIL_WORDS.test(text)) return askFor('email');
      if (text === R.CALL_ME_LABEL || CALL_ME_WORDS.test(text)) return askFor('phone');
      return { ...fallbackTurn(ctx.personality, ctx.presets), context: CLEAR_CONTACT_FLOW };
    }

    if (step === 'email' || step === 'phone') {
      const usable = typed && (step === 'email' ? typed.email : typed.phone) ? typed : null;
      if (!usable) {
        const attempts = (draft.attempts ?? 0) + 1;
        const filedEmail = draft.handoffContact?.email;
        if (attempts >= MAX_CONTACT_ATTEMPTS && filedEmail) {
          return { messages: [R.KEEPING_ACCOUNT_EMAIL(filedEmail)], quickButtons: menuButtons(ctx.presets), context: CLEAR_CONTACT_FLOW };
        }
        return {
          messages: [step === 'email' ? R.INVALID_EMAIL : R.INVALID_PHONE],
          quickButtons: buttonFor(ctx.presets, 'help', 'Back to menu'),
          context: { contactDraft: { ...draft, attempts } },
        };
      }
      return fileChange(ctx, draft, usable);
    }

    // step === 'contact': no account email, so nothing is filed until they give a way to reach them.
    if (!typed) {
      return {
        messages: [R.INVALID_CONTACT],
        quickButtons: buttonFor(ctx.presets, 'help', 'Back to menu'),
        context: { contactDraft: { ...draft, attempts: (draft.attempts ?? 0) + 1 } },
      };
    }
    const result = await submitTriage(ctx, draft.reason ?? 'talk_to_human', draft.summary, typed, draft.priority);
    if (!result.accepted) {
      return { messages: [R.TRIAGE_UNAVAILABLE], quickButtons: menuButtons(ctx.presets), context: CLEAR_CONTACT_FLOW };
    }
    return {
      messages: [result.mode === 'noop' ? R.TRIAGE_NOTED : filedMessage(typed, result.ticketRef)],
      quickButtons: menuButtons(ctx.presets),
      context: CLEAR_CONTACT_FLOW,
    };
  },
};

/** Re-files the already-open ticket with a new contact and confirms where support will now reply. */
async function fileChange(ctx: PresetContext, draft: ContactDraft, contact: TriageContact): Promise<BotTurn> {
  const result = await submitTriage(ctx, draft.reason ?? 'talk_to_human', draft.summary, contact, draft.priority);
  if (!result.accepted) {
    return { messages: [R.TRIAGE_UNAVAILABLE], quickButtons: menuButtons(ctx.presets), context: CLEAR_CONTACT_FLOW };
  }
  const message = result.mode === 'noop'
    ? R.TRIAGE_NOTED
    : contact.phone ? R.HANDOFF_PHONE(contact.phone, result.ticketRef) : R.CONTACT_UPDATED_EMAIL(String(contact.email));
  return { messages: [message], quickButtons: menuButtons(ctx.presets), context: CLEAR_CONTACT_FLOW };
}

const reportProblem: Preset = {
  id: 'report_problem',
  label: 'Report a problem',
  showInMenu: true,
  priority: 30,
  keywords: ['problem', 'issue', 'bug', 'wrong', 'broken', 'error', 'complaint', 'complain', 'not working', 'failed', 'missing'],
  patterns: [/something('s| is) wrong/, /(doesn't|does not|isn't|is not|won't|will not) (work|load|open)/],
  async handler(ctx, input) {
    const pending = ctx.session.context.pendingIntentId === 'report_problem';
    if (!pending) {
      return { messages: [R.reportProblemPrompt(ctx.personality)], quickButtons: [], context: { pendingIntentId: 'report_problem' } };
    }

    let caseContext: Partial<SessionContext> = {};
    if (input.caseRefNo) {
      const found = await ctx.caseDetails.findByRefNo(input.caseRefNo).catch(() => null);
      if (found) caseContext = { selectedCaseRefNo: found.caseRefNo, selectedCaseId: found.id };
    }

    // File it with the account email now; the hand-off line says where and when support replies.
    const handoff = await handOffToSupport({ ...ctx, session: { ...ctx.session, context: { ...ctx.session.context, ...caseContext } } }, { summary: input.text, reason: 'report_problem' });
    return { ...handoff, context: { ...caseContext, ...(handoff.context || {}) } };
  },
};

const help: Preset = {
  id: 'help',
  label: 'What can you help with?',
  showInMenu: true,
  priority: 1,
  keywords: ['help', 'menu', 'options', 'what can you do', 'start'],
  patterns: [/what can you (do|help)/],
  async handler(ctx) {
    return { messages: [R.menuIntro(ctx.personality)], quickButtons: menuButtons(ctx.presets), context: CLEAR_CONTACT_FLOW };
  },
};

const greeting: Preset = {
  id: 'greeting',
  label: 'Hello',
  showInMenu: false,
  priority: 2,
  keywords: [],
  patterns: [/^(hi|hello|hey|hiya|yo|good (morning|afternoon|evening))\b/],
  async handler(ctx) {
    return { messages: [R.randomGreeting(ctx.personality, ctx.user.firstName), R.menuIntro(ctx.personality)], quickButtons: menuButtons(ctx.presets) };
  },
};

const thanks: Preset = {
  id: 'thanks',
  label: 'Thanks',
  showInMenu: false,
  priority: 3,
  keywords: [],
  patterns: [/\b(thanks|thank you|thx|ty|cheers|appreciate it)\b/],
  async handler(ctx) {
    return {
      messages: [R.thanks(ctx.personality, ctx.user.firstName)],
      quickButtons: menuButtons(ctx.presets),
    };
  },
};

/**
 * "Bye" / "that's all" / "end chat": Evie signs off and the engine flags the turn so the widget can
 * collect end-of-chat feedback, archive the session and close. Patterns only, so "done" or "stop"
 * inside a longer sentence never ends the chat by accident.
 */
const endChat: Preset = {
  id: END_CHAT_PRESET_ID,
  label: 'End chat',
  showInMenu: false,
  priority: 4,
  keywords: [],
  patterns: [
    /^(bye|goodbye|bye bye|see ya|see you|cya|ciao|good night)\b/,
    /\b(end|close|finish|stop|exit|quit|leave) (the |this |our |my )?(chat|conversation|session)\b/,
    /^(no,? )?(thanks?|thank you)?,? ?(that's|thats|that is|that'll be|that will be) (all|it|everything)\b/,
    /^(no,? |nope,? )?(i'm|im|i am|we're|were|all) (done|finished|sorted|all set)( here| now| for now| for today| thanks| thank you)?$/,
    /^(no,? |nope,? )?nothing else( thanks| thank you| for now| for today)?$/,
  ],
  async handler(ctx) {
    return { messages: [R.goodbye(ctx.personality, ctx.user.firstName)], quickButtons: [], context: CLEAR_CONTACT_FLOW };
  },
};

/** Order matters only for the menu; matching uses scores and `priority`. */
export const PRESETS: Preset[] = [
  caseStatus,
  casesForApproval,
  casesInProgress,
  howToTrack,
  reportProblem,
  contactSupport,
  help,
  caseDelay,
  whoToContact,
  caseQuestion,
  caseLookup,
  greeting,
  thanks,
  endChat,
];

export const fallbackTurn = (personality: Personality, presets: Preset[]): BotTurn => ({
  messages: [R.fallback(personality)],
  quickButtons: menuButtons(presets),
});

/**
 * No preset (or LLM) could answer. Offers the design team when a case is in context, and a support
 * call-back otherwise; the unanswered text seeds the ticket.
 */
export const unresolvedTurn = (presets: Preset[], hasSelectedCase: boolean): BotTurn => ({
  messages: [R.unresolvedPrompt(hasSelectedCase)],
  quickButtons: [
    ...(hasSelectedCase ? buttonFor(presets, WHO_TO_CONTACT_PRESET_ID) : []),
    ...buttonFor(presets, CONTACT_SUPPORT_PRESET_ID),
    ...buttonFor(presets, 'help'),
  ],
  context: { helpAttempted: true, escalationUnlocked: true },
});

/** The user is clearly frustrated and nothing answered: offer a person straight away. */
export const escalationOfferTurn = (presets: Preset[], hasSelectedCase: boolean, circling: boolean): BotTurn => ({
  messages: [R.escalationAck(circling)],
  quickButtons: [
    ...(hasSelectedCase ? buttonFor(presets, WHO_TO_CONTACT_PRESET_ID) : []),
    ...buttonFor(presets, CONTACT_SUPPORT_PRESET_ID),
    ...buttonFor(presets, 'help', 'Back to menu'),
  ],
  context: { helpAttempted: true, escalationUnlocked: true, pendingIntentId: undefined },
});

export const greetingTurn = (personality: Personality, firstName: string, presets: Preset[]): BotTurn => ({
  messages: [R.randomGreeting(personality, firstName), R.menuIntro(personality)],
  quickButtons: menuButtons(presets),
});

export const errorTurn = (personality: Personality, presets: Preset[]): BotTurn => ({
  messages: [R.casesError(personality)],
  quickButtons: menuButtons(presets),
});

export { PRESET_BUTTON_PREFIX, CASE_BUTTON_PREFIX };
